import { getAllLegalMoves, applyMove, inCheck, getPieceType } from "./engine";

// ─────────────────────────────────────────
//  PIECE-SQUARE TABLES  (white perspective, row 0 = rank 8)
// ─────────────────────────────────────────

const PAWN_PST = [
   0,  0,  0,  0,  0,  0,  0,  0,
  50, 50, 50, 50, 50, 50, 50, 50,
  10, 10, 20, 30, 30, 20, 10, 10,
   5,  5, 10, 25, 25, 10,  5,  5,
   0,  0,  0, 20, 20,  0,  0,  0,
   5, -5,-10,  0,  0,-10, -5,  5,
   5, 10, 10,-20,-20, 10, 10,  5,
   0,  0,  0,  0,  0,  0,  0,  0,
];

const KNIGHT_PST = [
  -50,-40,-30,-30,-30,-30,-40,-50,
  -40,-20,  0,  0,  0,  0,-20,-40,
  -30,  0, 10, 15, 15, 10,  0,-30,
  -30,  5, 15, 20, 20, 15,  5,-30,
  -30,  0, 15, 20, 20, 15,  0,-30,
  -30,  5, 10, 15, 15, 10,  5,-30,
  -40,-20,  0,  5,  5,  0,-20,-40,
  -50,-40,-30,-30,-30,-30,-40,-50,
];

const BISHOP_PST = [
  -20,-10,-10,-10,-10,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5, 10, 10,  5,  0,-10,
  -10,  5,  5, 10, 10,  5,  5,-10,
  -10,  0, 10, 10, 10, 10,  0,-10,
  -10, 10, 10, 10, 10, 10, 10,-10,
  -10,  5,  0,  0,  0,  0,  5,-10,
  -20,-10,-10,-10,-10,-10,-10,-20,
];

const ROOK_PST = [
   0,  0,  0,  0,  0,  0,  0,  0,
   5, 10, 10, 10, 10, 10, 10,  5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
   0,  0,  0,  5,  5,  0,  0,  0,
];

const QUEEN_PST = [
  -20,-10,-10, -5, -5,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5,  5,  5,  5,  0,-10,
   -5,  0,  5,  5,  5,  5,  0, -5,
    0,  0,  5,  5,  5,  5,  0, -5,
  -10,  5,  5,  5,  5,  5,  0,-10,
  -10,  0,  5,  0,  0,  0,  0,-10,
  -20,-10,-10, -5, -5,-10,-10,-20,
];

const KING_PST = [
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -20,-30,-30,-40,-40,-30,-30,-20,
  -10,-20,-20,-20,-20,-20,-20,-10,
   20, 20,  0,  0,  0,  0, 20, 20,
   20, 30, 10,  0,  0, 10, 30, 20,
];

const PIECE_VAL = { pawn: 100, knight: 320, bishop: 330, rook: 500, queen: 900, king: 20000 };

// Mirror a square vertically for black's perspective
function mirror(sq) { return (7 - Math.floor(sq / 8)) * 8 + (sq % 8); }

// ─────────────────────────────────────────
//  EVALUATION
// ─────────────────────────────────────────

function evaluateBoard(board) {
  let score = 0;
  board.white.pawns.forEach(sq   => { score += PIECE_VAL.pawn   + PAWN_PST[sq]; });
  board.white.knights.forEach(sq => { score += PIECE_VAL.knight + KNIGHT_PST[sq]; });
  board.white.bishops.forEach(sq => { score += PIECE_VAL.bishop + BISHOP_PST[sq]; });
  board.white.rooks.forEach(sq   => { score += PIECE_VAL.rook   + ROOK_PST[sq]; });
  board.white.queens.forEach(sq  => { score += PIECE_VAL.queen  + QUEEN_PST[sq]; });
  board.white.king.forEach(sq    => { score += PIECE_VAL.king   + KING_PST[sq]; });

  board.black.pawns.forEach(sq   => { score -= PIECE_VAL.pawn   + PAWN_PST[mirror(sq)]; });
  board.black.knights.forEach(sq => { score -= PIECE_VAL.knight + KNIGHT_PST[mirror(sq)]; });
  board.black.bishops.forEach(sq => { score -= PIECE_VAL.bishop + BISHOP_PST[mirror(sq)]; });
  board.black.rooks.forEach(sq   => { score -= PIECE_VAL.rook   + ROOK_PST[mirror(sq)]; });
  board.black.queens.forEach(sq  => { score -= PIECE_VAL.queen  + QUEEN_PST[mirror(sq)]; });
  board.black.king.forEach(sq    => { score -= PIECE_VAL.king   + KING_PST[mirror(sq)]; });

  return score;
}

// ─────────────────────────────────────────
//  MOVE ORDERING  (for alpha-beta efficiency)
// ─────────────────────────────────────────

function orderMoves(moves, board, color) {
  const enemy = color === "white" ? "black" : "white";
  return moves.map(m => {
    let priority = 0;
    const victimType = getPieceType(m.to, enemy, board);
    if (victimType) {
      // MVV-LVA: most valuable victim, least valuable attacker
      priority += PIECE_VAL[victimType] * 10 - PIECE_VAL[m.type];
    }
    const isPromo = m.type === "pawn" && (Math.floor(m.to / 8) === 0 || Math.floor(m.to / 8) === 7);
    if (isPromo) priority += 800;
    // Bonus for center control
    if ([27,28,35,36].includes(m.to)) priority += 20;
    if ([18,19,20,21,26,29,34,37,42,43,44,45].includes(m.to)) priority += 10;
    return { m, priority };
  }).sort((a, b) => b.priority - a.priority).map(x => x.m);
}

// ─────────────────────────────────────────
//  MINIMAX WITH ALPHA-BETA
// ─────────────────────────────────────────

function minimax(board, depth, alpha, beta, isMaximizingWhite, useOrdering) {
  if (depth === 0) return evaluateBoard(board);

  const color = isMaximizingWhite ? "white" : "black";
  let moves = getAllLegalMoves(color, board);

  if (moves.length === 0) {
    if (inCheck(color, board)) return isMaximizingWhite ? -99000 : 99000;
    return 0; // stalemate
  }

  if (useOrdering) moves = orderMoves(moves, board, color);

  if (isMaximizingWhite) {
    let best = -Infinity;
    for (const move of moves) {
      const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 0;
      const { board: nb } = applyMove(board, move.from, move.type, "white", move.to, isPromo ? "queen" : null);
      const val = minimax(nb, depth - 1, alpha, beta, false, useOrdering);
      best = Math.max(best, val);
      alpha = Math.max(alpha, val);
      if (beta <= alpha) break;
    }
    return best;
  } else {
    let best = Infinity;
    for (const move of moves) {
      const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 7;
      const { board: nb } = applyMove(board, move.from, move.type, "black", move.to, isPromo ? "queen" : null);
      const val = minimax(nb, depth - 1, alpha, beta, true, useOrdering);
      best = Math.min(best, val);
      beta = Math.min(beta, val);
      if (beta <= alpha) break;
    }
    return best;
  }
}

// ─────────────────────────────────────────
//  DIFFICULTY CONFIGS
// ─────────────────────────────────────────

export const DIFFICULTIES = {
  easy:         { label: "Easy",         depth: 0, useOrdering: false, topN: null, random: true  },
  novice:       { label: "Novice",       depth: 1, useOrdering: false, topN: 5,    random: true  },
  intermediate: { label: "Intermediate", depth: 2, useOrdering: false, topN: 3,    random: true  },
  advanced:     { label: "Advanced",     depth: 3, useOrdering: true,  topN: 2,    random: false },
  pro:          { label: "Pro",          depth: 3, useOrdering: true,  topN: 1,    random: false },
};

// ─────────────────────────────────────────
//  PUBLIC: GET BOT MOVE
// ─────────────────────────────────────────

// BLANCHE always plays as White
export function getBlancheMove(board, difficulty = "advanced") {
  const config = DIFFICULTIES[difficulty] || DIFFICULTIES.advanced;
  const moves = getAllLegalMoves("white", board);
  if (!moves.length) return null;

  if (config.random && config.depth === 0) {
    return moves[Math.floor(Math.random() * moves.length)];
  }

  const scored = moves.map(move => {
    const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 0;
    const { board: nb, capturedType } = applyMove(board, move.from, move.type, "white", move.to, isPromo ? "queen" : null);
    let score;
    if (config.depth === 1) {
      score = evaluateBoard(nb);
      if (capturedType) score += PIECE_VAL[capturedType] || 0;
      if (isPromo) score += 400;
      if (inCheck("black", nb)) score += 15;
      if ([27,28,35,36].includes(move.to)) score += 8;
      score += Math.random() * 10;
    } else {
      score = minimax(nb, config.depth - 1, -Infinity, Infinity, false, config.useOrdering);
      if (config.random) score += Math.random() * 20;
    }
    return { move, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const pool = scored.slice(0, config.topN || scored.length);
  return pool[Math.floor(Math.random() * pool.length)].move;
}

export function getBotMove(board, difficulty = "novice") {
  const config = DIFFICULTIES[difficulty] || DIFFICULTIES.novice;
  const moves = getAllLegalMoves("black", board);
  if (!moves.length) return null;

  // Easy: fully random
  if (config.random && config.depth === 0) {
    return moves[Math.floor(Math.random() * moves.length)];
  }

  // Score all moves
  const scored = moves.map(move => {
    const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 7;
    const { board: nb } = applyMove(board, move.from, move.type, "black", move.to, isPromo ? "queen" : null);

    let score;
    if (config.depth === 1) {
      // Novice: single-ply heuristic
      score = -evaluateBoard(nb);
      // Add small heuristics
      const { capturedType } = applyMove(board, move.from, move.type, "black", move.to, isPromo ? "queen" : null);
      if (capturedType) score += PIECE_VAL[capturedType] || 0;
      if (isPromo) score += 400;
      if (inCheck("white", nb)) score += 15;
      if ([27,28,35,36].includes(move.to)) score += 8;
      score += Math.random() * 10; // some noise for unpredictability
    } else {
      // Intermediate / Advanced / Pro: minimax
      score = -minimax(nb, config.depth - 1, -Infinity, Infinity, true, config.useOrdering);
      if (config.random) score += Math.random() * 20;
    }
    return { move, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const pool = scored.slice(0, config.topN || scored.length);
  return pool[Math.floor(Math.random() * pool.length)].move;
}
